import { apiClient } from "@/shared/services/api";
import type { ApiResponse } from "@/features/auth/services/authApi";

export type OrderDiscountStatus = "Draft" | "PendingApproval" | "Approved" | "Rejected" | "Cancelled" | "Superseded";
export type OrderDiscountKind = "Percentage" | "FixedAmount";

export interface OrderDiscountRequest {
  id: string;
  orderId: string;
  orderNumber: string;
  customerName: string;
  status: OrderDiscountStatus;
  kind: OrderDiscountKind;
  value: number;
  reason: string;
  rejectionReason: string | null;
  reviewNotes: string | null;
  existingDiscountReference: string | null;
  goodsSubtotal: number;
  existingDiscountAmount: number;
  currentGoodsPayable: number;
  proposedDiscountAmount: number;
  proposedGoodsPayable: number;
  incrementalReduction: number;
  requestedByUserId: string;
  requesterName: string;
  submittedAt: string | null;
  reviewedByUserId: string | null;
  reviewedAt: string | null;
}

export interface OrderDiscountQuote {
  goodsSubtotal: number;
  existingDiscountAmount: number;
  currentGoodsPayable: number;
  proposedDiscountAmount: number;
  proposedGoodsPayable: number;
  incrementalReduction: number;
  equivalentPercentage: number;
}

export interface OrderDiscountInput {
  kind: OrderDiscountKind;
  value: number;
  reason: string;
}

export const orderDiscountApprovalsApi = {
  forOrder: async (orderId: string) => {
    const { data } = await apiClient.get<ApiResponse<{ items: OrderDiscountRequest[]; totalCount: number }>>(
      `/orders/${orderId}/discount-requests`, { params: { pageNumber: 1, pageSize: 20 } });
    return data.data;
  },
  list: async (status: OrderDiscountStatus, search: string, pageNumber: number, pageSize: number) => {
    const { data } = await apiClient.get<ApiResponse<{ items: OrderDiscountRequest[]; totalCount: number; pageNumber: number; pageSize: number }>>(
      "/discount-approvals", { params: { status, search: search || undefined, pageNumber, pageSize } });
    return data.data;
  },
  detail: async (id: string) => {
    const { data } = await apiClient.get<ApiResponse<OrderDiscountRequest>>(`/discount-approvals/${id}`);
    return data.data;
  },
  quote: async (orderId: string, input: OrderDiscountInput) => {
    const { data } = await apiClient.post<ApiResponse<OrderDiscountQuote>>(`/orders/${orderId}/discount-requests/quote`, input);
    return data.data;
  },
  submit: async (orderId: string, input: OrderDiscountInput) => {
    const { data } = await apiClient.post<ApiResponse<OrderDiscountRequest>>(`/orders/${orderId}/discount-requests`, input);
    return data.data;
  },
  decide: async (id: string, action: "approve" | "reject", rejectionReason?: string) => {
    const { data } = await apiClient.post<ApiResponse<OrderDiscountRequest>>(`/discount-approvals/${id}/${action}`,
      { notes: null, rejectionReason: rejectionReason || null });
    return data.data;
  },
};
